import {
  collection,
  doc,
  DocumentData,
  getDoc,
  getDocs,
  setDoc,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { DashboardRoutePath } from 'modules/dashboard/navigation';
import React from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useHistory, useParams } from 'react-router-dom';
import { auth, firestore, getQuery, timeStampToDate, timeStampToTime } from 'services';
import { Button, Input, SelectForm, Splash } from 'shared/Components';
import { Box, FlexContainer, Image, Text } from 'shared/Typhography';
import styled from 'styled-components';

const Container = styled.div`
  margin-top: 150px;
  padding-bottom: 100px;
  max-height: 100vh;
`;

const FormContainer = styled.div`
  flex: 1;
  padding: 16px;
  box-sizing: border-box;
`;

const HistoryContainer = styled.div`
  width: 400px;
  padding: 16px;
  box-sizing: border-box;

  overflow-y: auto;
`;

const StatusBadge = styled.span`
  color: white;
  padding: 5px;
  border-radius: 4px;
  width: max-content;
  box-sizing: border-box;
  height: 30px;

  &.requested {
    background: orange;
  }
  &.pending {
    background: #d5d55f;
  }
  &.approved {
    background: #30af30;
  }
  &.rejected {
    background: red;
  }
  &.completed {
    background: #30af30;
  }
`;

type EventType = 'status' | 'approvedAmount' | 'payoutMethod' | 'comment' | 'reject';

interface RawHistoryItem {
  actor: {
    id: string;
    name: string;
    profileImage: string;
  };
  createdAt: Timestamp;
  updatedAttributes: EventType[];
  updates: {
    value: string | number | null;
    previousValue: string | number | null;
    attribute: EventType;
  }[];
}

interface HistoryItem {
  actor: {
    id: string;
    name: string;
    profileImage: string;
  };
  createdAt: Timestamp;
  type: EventType;
  value: string | number | null;
  previousValue: string | number | null;
}

interface FormInputs {
  paymentMethod?: string | null;
  approvedAmount?: number | null;
  status: any;
  comment?: string | null;
}

interface PayoutRequest extends DocumentData {
  createdAt: Timestamp;
  updatedAt: Timestamp;
  status: 'requested' | 'pending' | 'approved' | 'rejected' | 'completed';
  requestedAmount: number;
  paymentMethod?: string;
  approvedAmount?: number;
  artist: {
    id: string;
    profileImageUrl: string;
    name: string;
  };
  requestedBy: {
    id: string;
    profileImageUrl: string;
    name: string;
  };
  history: HistoryItem[];
}

export default function PayoutRequestDetailsScreen() {
  const user = auth.currentUser;
  const { id } = useParams<{ id: string }>();
  const navigate = useHistory();

  //const [isLoading, setLoading] = React.useState<boolean>(true);
  const [payout, setPayout] = React.useState<PayoutRequest>();
  const [userDoc, setUserDoc] = React.useState<{
    id: string;
    name: string;
    profileImage: string;
  }>();

  const { handleSubmit, control } = useForm<FormInputs>();

  const fetch = async () => {
    if (!user) return;

    const userDoc = await getDoc(doc(firestore, `usersWeb/${user.uid}`));
    setUserDoc({
      id: user.uid,
      name: userDoc.data()?.name ?? userDoc.data()?.Name ?? '-',
      profileImage: userDoc.data()?.profileImageUrl ?? '-',
    });

    const tPayout = await getDoc(doc(firestore, `payoutRequests/${id}`));
    const tHistory = await getDocs(
      getQuery(`payoutRequests/${id}/history`, [], [{ field: 'createdAt', type: 'desc' }]),
    );
    const ttHistoryMapped = tHistory.docs.map((item) => {
      const pItem = item.data() as RawHistoryItem;
      return pItem.updates.map(
        (event) =>
          ({
            actor: pItem.actor,
            createdAt: pItem.createdAt,
            value: event.value,
            previousValue: event.previousValue,
            type: event.attribute,
          } as HistoryItem),
      );
    });

    const history: HistoryItem[] = [];
    ttHistoryMapped.forEach((tHistoryItem) => history.push(...tHistoryItem));

    setPayout({
      ...(tPayout.data() as PayoutRequest),
      history: history.filter((item) => item.previousValue != item.value),
    });
  };

  const onSubmit = handleSubmit(async (data: FormInputs) => {
    data.status = data?.status?.value;

    const sendData: FormInputs = {
      paymentMethod: null,
      approvedAmount: null,
      status: null,
      comment: null,
    };

    const keys = Object.keys(data);
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i] as keyof FormInputs;

      if (data[key]?.length == 0) data[key] = null;

      if (key === 'comment') {
        if (data.comment) sendData.comment = data.comment;
        else delete sendData.comment;
      } else {
        if (payout?.[key] !== (key === 'approvedAmount' ? Number(data[key]) : data[key]))
          sendData[key] = key === 'approvedAmount' ? Number(data[key]) : data[key];
        else delete sendData[key];
      }
    }

    try {
      const docRef = doc(firestore, `payoutRequests/${id}`);
      await updateDoc(docRef, {
        ...JSON.parse(
          JSON.stringify({
            paymentMethod: sendData.paymentMethod,
            approvedAmount: sendData.approvedAmount,
            status: sendData.status,
          }),
        ),
        updatedAt: Timestamp.now(),
      });
    } catch (err) {
      console.log(err);
      toast.error('Failed to update payout request!');
    }

    try {
      const attributes = Object.keys(sendData);
      const docRef = collection(firestore, `payoutRequests/${id}/history`);
      await setDoc(doc(docRef), {
        actor: {
          id: userDoc?.id,
          name: userDoc?.name,
          profileImage: userDoc?.profileImage,
        },
        createdAt: Timestamp.now(),
        updatedAttributes: attributes,
        updates: attributes.map((key) => ({
          attribute: key === 'paymentMethod' ? 'payoutMethod' : key,
          previousValue: key !== 'comment' ? payout?.[key] : null,
          value: sendData?.[key as keyof FormInputs],
        })),
      });
    } catch (err) {
      console.log(err);
      toast.error('Failed to create history item!');
    }

    navigate.push(`/${DashboardRoutePath.PAYOUT_REQUESTS}`);
  });

  React.useEffect(() => {
    if (payout) return;

    fetch();
  }, []);

  console.log('payout', payout);

  return (
    <Container>
      <Box padding="16px">
        {!payout && <Splash />}
        {payout && (
          <>
            <FlexContainer flexDirection="row" width="100%" justifyContent="space-between">
              <FlexContainer flexDirection="column" flex="1">
                <FlexContainer flexWrap="wrap">
                  <Image
                    cursor="pointer"
                    src={payout.artist.profileImageUrl}
                    borderRadiusType="circle"
                    size="insightsThumbnail"
                    alt="Picture"
                    margin={{ right: '20px' }}
                  />
                  <FlexContainer flexDirection="column" alignItems="flex-start">
                    <Text size={2.5} margin="0 0 5px 0">
                      {payout.artist.name}
                    </Text>
                    <Text size={1.8} weight="bold">
                      Requested ${payout.requestedAmount.toFixed(2)}
                    </Text>
                  </FlexContainer>
                </FlexContainer>
              </FlexContainer>
              <FlexContainer flexDirection="column" flex="1">
                <Text textAlign="right">
                  Requested by &nbsp; {payout.requestedBy.name} at &nbsp;
                  {`${timeStampToTime(payout?.createdAt)} ${timeStampToDate(payout?.createdAt)}`}
                </Text>
              </FlexContainer>
            </FlexContainer>
            <FlexContainer flexDirection="row" width="100%" maxHeight="100vh" overflowY="hidden">
              <FormContainer>
                <Text>Payout Controle</Text>
                <form
                  style={{ marginTop: '50px' }}
                  onSubmit={onSubmit}
                  onKeyPress={(event) => {
                    if (event.key == 'Enter') event.preventDefault();
                  }}
                >
                  <Input
                    required
                    label="Payout Method"
                    placeholder="Enter Payout Method"
                    name="paymentMethod"
                    type="text"
                    control={control}
                    width="100%"
                    marginBottom={20}
                    marginRight={30}
                    margin="0 auto 40px"
                    defaultValue={payout?.paymentMethod}
                  />
                  <Input
                    required
                    label="Approved Amount"
                    placeholder="Enter approved amount"
                    name="approvedAmount"
                    type="number"
                    control={control}
                    width="100%"
                    marginBottom={20}
                    marginRight={30}
                    margin="0 auto 40px"
                    defaultValue={payout?.approvedAmount}
                  />
                  <SelectForm
                    required
                    label="Status"
                    placeholder="Select Status"
                    name="status"
                    control={control}
                    values={[
                      { value: 'requested', label: 'Requested' },
                      { value: 'pending', label: 'Pending' },
                      { value: 'approved', label: 'Approved' },
                      { value: 'rejected', label: 'Rejected' },
                      { value: 'completed', label: 'Completed' },
                    ]}
                    width="100%"
                    margin="0 auto 40px"
                    defaultValue={[
                      { value: 'requested', label: 'Requested' },
                      { value: 'pending', label: 'Pending' },
                      { value: 'approved', label: 'Approved' },
                      { value: 'rejected', label: 'Rejected' },
                      { value: 'completed', label: 'Completed' },
                    ].find((item) => item?.value == payout?.status)}
                  />
                  <Input
                    required
                    label="Comment"
                    placeholder="Enter a comment"
                    name="comment"
                    type="textarea"
                    control={control}
                    width="100%"
                    height="200px"
                    marginBottom={20}
                    marginRight={30}
                    margin="0 auto 40px"
                  />
                  <FlexContainer justifyContent="center">
                    <Button width={300} height={58} type="submit">
                      Submit
                    </Button>
                  </FlexContainer>
                </form>
              </FormContainer>
              <HistoryContainer>
                {payout.history.map((item, index) => (
                  <EventBox key={`PayoutHIstoryItem-${index}`} historyItem={item} />
                ))}
              </HistoryContainer>
            </FlexContainer>
          </>
        )}
      </Box>
    </Container>
  );
}

const EventWrapper = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding: 5px;
  border: solid lightgrey 1px;
  border-radius: 4px;
  margin: 5px 0;
`;

const EventBox = ({ historyItem }: { historyItem: HistoryItem }) => {
  return (
    <EventWrapper>
      <FlexContainer
        flexDirection="row"
        flexWrap="wrap"
        width="100%"
        alignItems="flex-end"
        margin="0 0 10px 0"
      >
        <Image
          src={historyItem.actor.profileImage}
          borderRadiusType="circle"
          width={'32px'}
          height={'32px'}
          mobWidth={'32px'}
          mobHeight={'32px'}
          alt="Picture"
          margin={{ right: '10px' }}
        />
        <Text>{historyItem?.actor?.name}</Text>
      </FlexContainer>
      <>
        {['comment', 'reject'].includes(historyItem.type) && historyItem.value}
        {historyItem.type === 'status' && (
          <FlexContainer flexWrap="wrap" gap="5px" alignItems="center">
            Status changed from{' '}
            <StatusBadge className={historyItem.previousValue as string}>
              {historyItem.previousValue}
            </StatusBadge>{' '}
            to{' '}
            <StatusBadge className={historyItem.value as string}>{historyItem.value}</StatusBadge>
          </FlexContainer>
        )}

        {historyItem.type === 'approvedAmount' && historyItem.previousValue && historyItem.value && (
          <>
            Approved amount changed from ${(historyItem.previousValue as number).toFixed(2)} to $
            {(historyItem.value as number).toFixed(2)}
          </>
        )}
        {historyItem.type === 'approvedAmount' &&
          !historyItem.previousValue &&
          historyItem.value && (
            <>Approved amount set as ${(historyItem.value as number).toFixed(2)}</>
          )}
        {historyItem.type === 'approvedAmount' &&
          historyItem.previousValue &&
          !historyItem.value && <>Approved amount removed</>}

        {historyItem.type === 'payoutMethod' && historyItem.previousValue && historyItem.value && (
          <>
            Payout Method changed from {historyItem.previousValue} to {historyItem.value}
          </>
        )}
        {historyItem.type === 'payoutMethod' && !historyItem.previousValue && historyItem.value && (
          <>Payout Method set as {historyItem.value}</>
        )}
        {historyItem.type === 'payoutMethod' && historyItem.previousValue && !historyItem.value && (
          <>Payout Method removed</>
        )}
      </>

      <FlexContainer
        margin="10px 0 0 0"
        flexDirection="row"
        flexWrap="wrap"
        width="100%"
        justifyContent="flex-end"
      >
        <Text size={0.8} margin="">
          {timeStampToDate(historyItem?.createdAt)} {timeStampToTime(historyItem?.createdAt)}
        </Text>
      </FlexContainer>
    </EventWrapper>
  );
};
